import React from 'react';
import { Settings, Globe, Lock, Search, Wrench, Database } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

export type SettingsTab = 'general' | 'apis' | 'security' | 'seo' | 'development';

interface SettingsNavigationProps {
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
}

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
  badge?: string;
}

const tabs: TabConfig[] = [
  { id: 'general', label: 'Général', icon: Settings },
  { id: 'apis', label: 'APIs', icon: Database },
  { id: 'security', label: 'Sécurité', icon: Lock },
  { id: 'seo', label: 'SEO', icon: Globe },
  { id: 'development', label: 'Développement', icon: Wrench },
];

export function SettingsNavigation({ activeTab, onTabChange }: SettingsNavigationProps) {
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto">
        {/* Search bar */}
        <div className="px-4 py-4 sm:px-6 lg:px-8 border-b border-gray-200">
          <div className="relative max-w-md">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="search"
              placeholder="Rechercher dans les paramètres..."
              className="block w-full rounded-md border-0 py-2.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={twMerge(
                    'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium',
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon
                    className={twMerge(
                      'mr-2 h-5 w-5',
                      isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                    )}
                    aria-hidden="true"
                  />
                  <span>{tab.label}</span>
                  {tab.badge && (
                    <span
                      className={twMerge(
                        'ml-2 rounded-full px-2.5 py-0.5 text-xs font-medium',
                        isActive
                          ? 'bg-blue-100 text-blue-600'
                          : 'bg-gray-100 text-gray-900'
                      )}
                    >
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
}

export default SettingsNavigation;
